import { useQuery } from '@tanstack/react-query';
import { BuildCheck } from './BuildCheck.js';
import { Navigate, useNavigate } from 'react-router-dom';
import type { AdminChildProfile } from '@qq/shared';
import { api } from '../lib/api.js';
import { useLogout, useSession } from '../lib/session.js';

interface Health {
  ok: boolean;
  database: string;
  aiEnabled: boolean;
  timezone: string;
  learningDay: string;
  build?: string;
}

interface AdminHealth {
  totalAttempts: number;
  /**
   * Whether this database has the seed definitions the running code ships.
   *
   * Nothing re-seeds when the server starts, so a deploy that changes the
   * curriculum ships that change doing nothing at all until someone runs the
   * re-seed. That is not a hypothetical - the per-skill band scopes went out
   * with the migration applied, the tests green, and all 96 live skill rows
   * still holding the empty default.
   */
  seed: {
    drifted: boolean;
    code: string;
    database: string | null;
    lastSeededAt: string | null;
  };
}

/**
 * Phase 1 admin: proof the parent session works and the stack is healthy.
 * The six-questions dashboard is Phase 12 - this page deliberately claims
 * nothing about the children's learning, because nothing is measured yet.
 */
export function AdminHome() {
  const navigate = useNavigate();
  const session = useSession();
  const logout = useLogout('parent');

  const children = useQuery({
    queryKey: ['admin', 'children'],
    queryFn: () => api<AdminChildProfile[]>('/admin/children'),
    enabled: Boolean(session.data?.parent),
  });

  const adminHealth = useQuery({
    queryKey: ['admin', 'health'],
    queryFn: () => api<AdminHealth>('/admin/health'),
    enabled: Boolean(session.data?.parent),
  });

  const health = useQuery({ queryKey: ['health'], queryFn: () => api<Health>('/health') });

  if (session.isPending) return <div className="admin">Loading…</div>;
  if (!session.data?.parent) return <Navigate to="/parent" replace />;

  return (
    <div className="admin">
      <header className="admin-header">
        <div>
          <h1>Quokka Quest</h1>
          <p className="muted">Signed in as {session.data.parent.name}</p>
        </div>
        <button
          type="button"
          className="btn btn--quiet"
          onClick={() =>
            logout.mutate(undefined, { onSuccess: () => navigate('/', { replace: true }) })
          }
        >
          Sign out
        </button>
      </header>

      <section className="card">
        <h2>This week</h2>
        <p className="muted">
          What they are getting better at, what is taking a while, what keeps coming up, and one
          thing to do with them for five minutes.
        </p>
        <button type="button" className="btn btn--big" onClick={() => navigate('/admin/dashboard')}>
          Open the dashboard
        </button>
      </section>

      <section className="card">
        <h2>Children</h2>
        {children.isPending && <p className="muted">Loading…</p>}
        <ul>
          {children.data?.map((child) => (
            <li key={child.id} style={{ marginBottom: 8 }}>
              <strong>{child.nickname}</strong> <span className="pill">{child.yearLevelLabel}</span>{' '}
              <span className="muted">born {child.dob}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="card">
        <h2>Curriculum</h2>
        <p className="muted">
          The skill graph, and the Australian Curriculum codes each point maps to. Every code is
          seeded unverified until you have checked it.
        </p>
        <button type="button" className="btn" onClick={() => navigate('/admin/curriculum')}>
          Open the curriculum map
        </button>
      </section>

      <section className="card">
        <h2>Progress against the curriculum</h2>
        <p>
          Where each child is, year by year &mdash; what they can do unaided, what they are part-way
          through, and what they have not met. Their own year is marked, so ahead and behind are
          visible without anyone being labelled.
        </p>
        <button type="button" className="btn" onClick={() => navigate('/admin/progress')}>
          See how they are tracking
        </button>
      </section>

      <section className="card">
        <h2>Between the two of them</h2>
        <p className="muted">
          Presence, invitations, shared adventures and the shelf view. Participation only - never
          levels, skills or scores.
        </p>
        <button type="button" className="btn" onClick={() => navigate('/admin/siblings')}>
          Open sibling settings
        </button>
      </section>

      <section className="card">
        <h2>How they are going</h2>
        <p className="muted">
          Each child is asked once a day how the day is going, and can write anything they want
          alongside it. Their words come here exactly as they wrote them. Nothing is scored or
          interpreted.
        </p>
        <button type="button" className="btn" onClick={() => navigate('/admin/checkins')}>
          Open check-ins
        </button>
      </section>

      <section className="card">
        <h2>Real-world quests</h2>
        <p className="muted">
          One short thing a day to do away from the screen &mdash; counting the pegs, reading the
          pantry, working out the change. Nothing is lost by skipping one.
        </p>
        <button type="button" className="btn" onClick={() => navigate('/admin/quests')}>
          Open the quest log
        </button>
      </section>

      <section className="card">
        <h2>Their writing</h2>
        <p className="muted">
          Free writing is never marked by the app. It waits here for you to read together.
        </p>
        <button type="button" className="btn" onClick={() => navigate('/admin/writing')}>
          Open the writing queue
        </button>
      </section>

      <section className="card">
        <h2>Sounds</h2>
        <p className="muted">
          Record the phonemes in your own voice. Phonics activities stay switched off until the
          clips they need exist and you have listened to them.
        </p>
        <button type="button" className="btn" onClick={() => navigate('/admin/audio')}>
          Open the audio manager
        </button>
      </section>

      <section className="card">
        <h2>System health</h2>
        <p className="muted">
          Database {health.data?.database ?? '…'} · AI content{' '}
          {health.data?.aiEnabled ? 'on' : 'off'} · {health.data?.timezone ?? '…'} · learning day{' '}
          {health.data?.learningDay ?? '…'}
        </p>
        <BuildCheck server={health.data?.build} />

        {adminHealth.data && (
          <p className="muted">
            Lesson content{' '}
            {adminHealth.data.seed.drifted ? (
              <span className="pill pill--warn">needs a re-seed</span>
            ) : (
              <span className="pill pill--ok">up to date</span>
            )}{' '}
            <code>{adminHealth.data.seed.code}</code>
            {adminHealth.data.seed.lastSeededAt &&
              ` · last loaded ${new Date(adminHealth.data.seed.lastSeededAt).toLocaleDateString()}`}
          </p>
        )}

        {adminHealth.data?.seed.drifted && (
          <p className="error-note">
            This app has been updated with new or changed lesson content that has not been loaded
            into your database yet. Everything still works on the content you have; the new material
            will not appear until it is loaded. Open the curriculum page and press{' '}
            <strong>Re-seed</strong>.
          </p>
        )}
      </section>

      <section className="card">
        <h2>Your data</h2>
        <p className="muted">
          Everything this app knows about your family, as one JSON file. It is yours.
        </p>
        <a className="btn btn--outline" href="/api/admin/export">
          Export everything
        </a>
        <p className="muted" style={{ marginTop: 12 }}>
          <a href="/api/admin/email/preview" target="_blank" rel="noreferrer">
            Preview the weekly email
          </a>{' '}
          · it goes out Sunday evening, and any failure emails you instead of failing quietly.
        </p>
      </section>
    </div>
  );
}
